import { call, put, takeEvery } from 'redux-saga/effects'; // eslint-disable-line
import { AUTH } from 'STORE/constants';
import { getUserData, saveLocalToken, getLocalToken, saveLocalUser, clearAuthSession } from 'HELPERS/auth';
import { push } from 'react-router-redux';

/**
 * Delays execution for a given time.
 * @param {number} ms - Milliseconds to delay.
 */
const delay = ms => new Promise(res => setTimeout(res, ms));

/* eslint-disable consistent-return */

/**
 * Generates a URL for Google authentication.
 * @returns {string} - The URL for Google authentication.
 */
function generateGoogleAuthUrl() {
  const redirectUrl = `${window.location.origin}/sign-in`;
  const cacheTime = new Date().getTime();

  return `${process.env.REACT_APP_API_HOST}/google/login?redirect_url=${encodeURIComponent(redirectUrl)}&nocache=${cacheTime}`;
}

/**
 * This function will be called when the user clicks on the "Sign in with Google" button.
 * It will generate the Google auth URL and redirect the page.
 */
export function* watchSignInRequest() {
  try {
    // Show loading for at least 0.5s for better user experience
    yield call(delay, 500);

    const url = generateGoogleAuthUrl();
    window.location = url;

    // Yielding URL for testability or future use
    return url;
  } catch (error) {
    // Dispatching failure action with error message
    yield put({ type: AUTH.SIGNIN.FAILURE, error: error.message });

    return null;
  }
}

export function* watchGetAuthenticatedUser() {
  try {
    const token = getLocalToken();
    if (!token) {
      yield put({ type: AUTH.FAIL_AUTHENTICATE });
      return yield put(push('/sign-in'));
    }
    return yield put({ type: AUTH.SIGNIN.COMPLETE, token });
  } catch (error) {
    yield put({ type: AUTH.FAIL_AUTHENTICATE });
    return yield put(push('/sign-in'));
  }
}

/**
 * This function will be called when the route matches with the sign-in?token=...
 * @param {object} token
 */
export function* watchSignInComplete({ token }) {
  try {
    const userResponse = yield call(getUserData, token);
    // 401 Unauthorized
    if (userResponse.status === 401) {
      // Redirect to sign in page
      yield put({ type: AUTH.SIGNIN.FAILURE });
      return yield put(push('/sign-in'));
    }
    // TODO - Handle different type of errors?
    if (userResponse.status !== 200) {
      yield put({ type: AUTH.SIGNIN.FAILURE });
      return yield put(push('/sign-in'));
    }

    const json = yield call([userResponse, 'json']);
    yield call(saveLocalToken, token);
    yield call(saveLocalUser, json);

    return yield put({ type: AUTH.SIGNIN.SUCCESS, payload: json.data });
  } catch (error) {
    yield put({ type: AUTH.SIGNIN.FAILURE });
    return yield put(push('/sign-in'));
  }
}

export function* watchSignInSuccess() {
  // TODO - Handle different target pages according to user type or first visit?
  // eslint-disable-next-line
  if (location.pathname.includes('/sign-in')) {
    return yield put(push('/control/date'));
  }
}

export function* watchSignOut() {
  yield call(clearAuthSession);
  return yield put(push('/sign-in'));
}

export default function* rootSagas() {
  yield takeEvery(AUTH.GET_USER, watchGetAuthenticatedUser);
  yield takeEvery(AUTH.SIGNIN.REQUEST, watchSignInRequest);
  yield takeEvery(AUTH.SIGNIN.COMPLETE, watchSignInComplete);
  yield takeEvery(AUTH.SIGNIN.SUCCESS, watchSignInSuccess);
  yield takeEvery(AUTH.SIGNOUT, watchSignOut);
}



// WEBPACK FOOTER //
// ./src/store/sagas/auth.sagas.js